<script setup lang="ts">
    import { Alert } from '@/components/ui/alert';
    import { Button } from '@/components/ui/button';
    import { Checkbox } from '@/components/ui/checkbox';
    import { computed } from 'vue';
    import { Head, useForm, usePage } from '@inertiajs/vue3';
    import { Input } from '@/components/ui/input';
    import { Label } from '@/components/ui/label';
    import { MicrosoftLogo } from '@/components/icons';
    import { useI18n } from 'vue-i18n';
    import AuthBase from '@/layouts/AuthLayout.vue';
    import TextLink from '@/components/TextLink.vue';
    import type { SharedData } from '@/types';

    defineProps<{
        status?: string;
        canResetPassword: boolean;
    }>();

    const page = usePage<SharedData>();
    const noCompany = computed(() => page.props.errors?.no_company);

    const { t } = useI18n();

    const form = useForm({
        email: '',
        password: '',
        remember: false,
    });

    const submit = () => {
        form.post(route('login'), {
            onFinish: () => form.reset('password'),
        });
    };

    const loginWithMicrosoft = () => {
        window.location.href = '/admin/login';
    };
</script>

<template>
    <AuthBase>
        <Head title="Log in" />

        <div
            v-if="status"
            class="mb-4 text-center text-sm font-medium text-green-600"
        >
            {{ status }}
        </div>

        <form
            @submit.prevent="submit()"
            class="flex flex-col gap-4"
        >
            <Alert
                v-if="form.errors.email"
                variant="error"
            >
                {{ t('auth.login_error') }}
            </Alert>
            <Alert
                v-if="noCompany"
                variant="error"
            >
                {{ t('auth.no_company') }}
            </Alert>
            <div class="grid gap-2">
                <Input
                    id="email"
                    type="email"
                    required
                    autofocus
                    :tabindex="1"
                    autocomplete="email"
                    v-model="form.email"
                    :placeholder="t('auth.email_placeholder')"
                    class="placeholder:text-foreground/80"
                />
            </div>

            <div class="grid gap-2">
                <Input
                    id="password"
                    type="password"
                    required
                    :tabindex="2"
                    autocomplete="current-password"
                    v-model="form.password"
                    :placeholder="t('auth.password_placeholder')"
                    class="placeholder:text-foreground/80"
                />
            </div>

            <Button
                type="submit"
                width="full"
                :tabindex="4"
                :disabled="form.processing"
                :loading="form.processing"
            >
                {{ t('auth.login_txt') }}
            </Button>

            <div class="flex items-center justify-between">
                <Label
                    for="remember"
                    class="flex items-center"
                >
                    <Checkbox
                        id="remember"
                        v-model="form.remember"
                        :tabindex="3"
                    />
                    <span>{{ t('auth.remember_me') }}</span>
                </Label>

                <TextLink
                    v-if="canResetPassword"
                    :href="route('password.request')"
                    class="text-xs"
                    :tabindex="5"
                >
                    {{ t('auth.forgot_password') }}
                </TextLink>
            </div>
        </form>
        <Button
            @click="loginWithMicrosoft"
            variant="outline"
            width="full"
            :tabindex="4"
            :disabled="form.processing"
        >
            <MicrosoftLogo />
            {{ t('auth.login_with_microsoft') }}
        </Button>
    </AuthBase>
</template>
